import { useState, useRef, useEffect } from "react";
import { useMessageStore } from "../../stores/messageStore";
import { useMemberStore } from "../../stores/memberStore";
import { useModalStore } from "../../stores/modalStore";
import { useAeThex } from "../../components/AeThexProvider";
import { ContextMenu } from "./ContextMenu.jsx";
import { useBookmarks } from "./modals/BookmarksModal.jsx";

const QUICK_REACTIONS = ['👍', '❤️', '😂', '🔥', '😮', '😢'];

// ── Markdown renderer ─────────────────────────────────────────────────────────

function renderInline(text, key = 0) {
  const re = /(`[^`\n]+`|\*\*[\s\S]+?\*\*|__[\s\S]+?__|~~[\s\S]+?~~|\*[^*\n]+\*|_[^_\n]+_|@\w+)/g;
  const parts = [];
  let last = 0, m, k = key * 1000;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**') || tok.startsWith('__')) {
      parts.push(<strong key={k++}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('~~')) {
      parts.push(<del key={k++}>{tok.slice(2, -2)}</del>);
    } else if (tok.startsWith('`')) {
      parts.push(<code key={k++} className="msg-code">{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('@')) {
      parts.push(<span key={k++} className="msg-mention">{tok}</span>);
    } else {
      parts.push(<em key={k++}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function MarkdownContent({ text }) {
  if (!text) return null;
  const segments = [];
  const codeBlockRe = /```(?:(\w+)\n)?([\s\S]*?)```/g;
  let last = 0, m, i = 0;
  while ((m = codeBlockRe.exec(text)) !== null) {
    if (m.index > last) segments.push({ t: 'inline', v: text.slice(last, m.index), i: i++ });
    segments.push({ t: 'block', lang: m[1] || '', v: m[2].replace(/^\n|\n$/g, ''), i: i++ });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ t: 'inline', v: text.slice(last), i: i++ });

  return (
    <span className="message-text">
      {segments.map(seg =>
        seg.t === 'block'
          ? <pre key={seg.i} className="msg-codeblock"><code>{seg.v}</code></pre>
          : <span key={seg.i}>{renderInline(seg.v, seg.i)}</span>
      )}
    </span>
  );
}

// ── Attachment renderer ───────────────────────────────────────────────────────

const IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i;
const AUDIO_EXT = /\.(mp3|wav|ogg|flac|m4a)(\?.*)?$/i;
const VIDEO_EXT = /\.(mp4|mov|webm|mkv)(\?.*)?$/i;
const URL_RE    = /https?:\/\/[^\s<>"]+/g;
const formatBytes = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

// ── URL Embed card ────────────────────────────────────────────────────────────
function EmbedCard({ url }) {
  const [embed, setEmbed] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`/api/embed?url=${encodeURIComponent(url)}`)
      .then(r => r.json())
      .then(d => { if (alive && d.title) setEmbed(d); else if (alive) setFailed(true); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [url]);

  if (failed || !embed) return null;

  return (
    <a href={url} target="_blank" rel="noreferrer" className="embed-card">
      {embed.image && <img src={embed.image} alt="" className="embed-card-img" onError={e => e.target.style.display='none'} />}
      <div className="embed-card-body">
        <div className="embed-card-site">{embed.siteName}</div>
        <div className="embed-card-title">{embed.title}</div>
        {embed.description && <div className="embed-card-desc">{embed.description}</div>}
      </div>
    </a>
  );
}

// ── URL Embeds (extracted from message text) ──────────────────────────────────
function UrlEmbeds({ text }) {
  const urls = [];
  let m;
  const re = new RegExp(URL_RE.source, 'g');
  while ((m = re.exec(text)) !== null) {
    const u = m[0].replace(/[.,;!?)]+$/, ''); // strip trailing punctuation
    if (!IMAGE_EXT.test(u) && !AUDIO_EXT.test(u) && !VIDEO_EXT.test(u)) urls.push(u);
  }
  const unique = [...new Set(urls)].slice(0, 3); // max 3 embeds per message
  if (!unique.length) return null;
  return <>{unique.map(u => <EmbedCard key={u} url={u} />)}</>;
}

// ── Inline media player ───────────────────────────────────────────────────────
function AudioPlayer({ src, name }) {
  return (
    <div className="media-player audio-player">
      <span className="media-icon">🎵</span>
      <div className="media-info">
        <div className="media-name">{name}</div>
        <audio controls preload="metadata" className="media-audio">
          <source src={src} />
        </audio>
      </div>
    </div>
  );
}

function VideoPlayer({ src, name }) {
  return (
    <div className="media-player video-player">
      <video controls preload="metadata" className="media-video" title={name}>
        <source src={src} />
      </video>
      <div className="media-name">{name}</div>
    </div>
  );
}

function Attachments({ attachments, text }) {
  // Detect bare image URLs in text
  const urlImages = [];
  if (text) {
    let m;
    const re = new RegExp(URL_RE.source, 'g');
    while ((m = re.exec(text)) !== null) {
      if (IMAGE_EXT.test(m[0])) urlImages.push(m[0]);
    }
  }

  const [lightbox, setLightbox] = useState(null);

  const allImages = [
    ...(attachments || []).filter(a => a.image),
    ...urlImages.map(url => ({ url, name: url.split('/').pop(), image: true })),
  ];
  const audioFiles = (attachments || []).filter(a => !a.image && AUDIO_EXT.test(a.name || ''));
  const videoFiles = (attachments || []).filter(a => !a.image && VIDEO_EXT.test(a.name || ''));
  const otherFiles = (attachments || []).filter(a => !a.image && !AUDIO_EXT.test(a.name || '') && !VIDEO_EXT.test(a.name || ''));

  if (!allImages.length && !audioFiles.length && !videoFiles.length && !otherFiles.length) return null;

  return (
    <>
      {allImages.length > 0 && (
        <div className="attachment-images">
          {allImages.map((a, i) => (
            <img key={i} src={a.url} alt={a.name} className="attachment-img"
              onClick={() => setLightbox(a.url)} title={a.name} />
          ))}
        </div>
      )}
      {audioFiles.map((a, i) => <AudioPlayer key={i} src={a.url} name={a.name} />)}
      {videoFiles.map((a, i) => <VideoPlayer key={i} src={a.url} name={a.name} />)}
      {otherFiles.length > 0 && (
        <div className="attachment-files">
          {otherFiles.map((a, i) => (
            <a key={i} href={a.url} target="_blank" rel="noreferrer" className="attachment-file-card" download={a.name}>
              <span className="attachment-file-icon">📄</span>
              <div className="attachment-file-info">
                <div className="attachment-file-name">{a.name}</div>
                {a.size && <div className="attachment-file-size">{formatBytes(a.size)}</div>}
              </div>
              <span className="attachment-file-dl">↓</span>
            </a>
          ))}
        </div>
      )}
      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="lightbox-img" onClick={e => e.stopPropagation()} />
          <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
        </div>
      )}
    </>
  );
}

// ── Poll renderer ─────────────────────────────────────────────────────────────

function PollMessage({ poll: initialPoll }) {
  const [poll, setPoll] = useState(initialPoll);
  const { user } = useAeThex();

  const totalVotes = poll.options.reduce((s, o) => s + o.votes.length, 0);
  const myVote     = poll.options.find(o => o.votes.includes(user?.id));
  const isClosed   = poll.closed || (poll.endsAt && new Date(poll.endsAt) < new Date());

  const handleVote = async (optionId) => {
    if (isClosed) return;
    try {
      const res = await fetch(`/api/polls/${poll.id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: JSON.stringify({ optionId }),
      });
      const d = await res.json();
      if (d.success) setPoll(d.poll);
    } catch (_) {}
  };

  return (
    <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10, padding: '12px 14px', marginTop: 4, maxWidth: 380 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 16 }}>📊</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{poll.question}</span>
        {isClosed && <span style={{ fontSize: 10, background: 'rgba(239,68,68,0.2)', color: '#fca5a5', padding: '1px 6px', borderRadius: 10, marginLeft: 'auto' }}>Closed</span>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {poll.options.map(opt => {
          const votes = opt.votes.length;
          const pct   = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
          const mine  = opt.votes.includes(user?.id);
          return (
            <div key={opt.id}>
              <button onClick={() => handleVote(opt.id)} disabled={isClosed}
                style={{ width: '100%', background: 'none', border: 'none', cursor: isClosed ? 'default' : 'pointer', padding: 0, textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: mine ? '#a5b4fc' : 'rgba(255,255,255,0.7)', fontWeight: mine ? 700 : 400 }}>
                    {mine ? '✓ ' : ''}{opt.text}
                  </span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{pct}%</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: mine ? '#6366f1' : 'rgba(99,102,241,0.45)', borderRadius: 3, transition: 'width 0.4s ease' }} />
                </div>
              </button>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 8 }}>
        {totalVotes} vote{totalVotes !== 1 ? 's' : ''}
        {poll.endsAt && !isClosed && ` · ends ${new Date(poll.endsAt).toLocaleString()}`}
      </div>
    </div>
  );
}

// ── User Hover Card ───────────────────────────────────────────────────────────

const STATUS_COLOR = { online: '#3ba55c', idle: '#faa61a', dnd: '#ed4245', offline: '#747f8d' };
const STATUS_LABEL = { online: 'Online', idle: 'Idle', dnd: 'Do Not Disturb', offline: 'Offline' };

function UserHoverCard({ member, anchorRef, onClose, onViewProfile, onMessage }) {
  const cardRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorRef.current || !cardRef.current) return;
    const anchor = anchorRef.current.getBoundingClientRect();
    const card   = cardRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top  = anchor.bottom + 6;
    let left = anchor.left;
    if (top + card.height > vh - 8)  top  = anchor.top - card.height - 6;
    if (left + card.width  > vw - 8) left = vw - card.width - 8;
    setPos({ top, left });
  }, [anchorRef]);

  useEffect(() => {
    const handler = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target) && anchorRef.current && !anchorRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [anchorRef, onClose]);

  const name   = member?.username || member?.name || member?.display_name || 'Unknown';
  const avatar = member?.avatar || name[0]?.toUpperCase() || '?';
  const status = member?.status || 'offline';
  const role   = member?.role || member?.badge;

  return (
    <div ref={cardRef} style={{
      position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999,
      background: 'rgba(14,14,18,0.98)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 10, padding: 14, width: 220, boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
      backdropFilter: 'blur(12px)',
    }}>
      {/* Banner strip */}
      <div style={{ height: 40, borderRadius: '8px 8px 0 0', background: 'linear-gradient(135deg,rgba(99,102,241,0.4),rgba(6,182,212,0.3))', margin: '-14px -14px 0', marginBottom: 0 }} />
      {/* Avatar */}
      <div style={{ position: 'relative', marginTop: -20, marginBottom: 8 }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff', border: '3px solid rgba(14,14,18,0.98)' }}>
          {avatar}
        </div>
        <span style={{ position: 'absolute', bottom: 1, left: 28, width: 12, height: 12, borderRadius: '50%', background: STATUS_COLOR[status], border: '2px solid rgba(14,14,18,0.98)', display: 'inline-block' }} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{name}</div>
      {role && <div style={{ fontSize: 10, color: '#a5b4fc', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{role}</div>}
      <div style={{ fontSize: 10, color: STATUS_COLOR[status], marginBottom: 10 }}>{STATUS_LABEL[status] || 'Offline'}</div>
      {member?.customStatus && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 10, fontStyle: 'italic' }}>"{member.customStatus}"</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={onViewProfile} style={{ flex: 1, padding: '5px 0', borderRadius: 6, background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
          View Profile
        </button>
        <button onClick={onMessage} style={{ flex: 1, padding: '5px 0', borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
          Message
        </button>
      </div>
    </div>
  );
}

function useHoverCard(delay = 600) {
  const [card, setCard] = useState(null); // { member, anchorRef }
  const timer = useRef(null);

  const onEnter = (member, anchorRef) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCard({ member, anchorRef }), delay);
  };
  const onLeave = () => { clearTimeout(timer.current); };
  const onClose = () => { clearTimeout(timer.current); setCard(null); };

  useEffect(() => () => clearTimeout(timer.current), []);

  return { card, onEnter, onLeave, onClose };
}

// ── Thread Reply Badge ────────────────────────────────────────────────────────

function ThreadReplyBadge({ count, lastReplyAt, onOpen }) {
  if (!count || count < 1) return null;
  const when = lastReplyAt
    ? (() => {
        const diff = Date.now() - new Date(lastReplyAt).getTime();
        if (diff < 60000) return 'just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        return new Date(lastReplyAt).toLocaleDateString();
      })()
    : null;
  return (
    <button onClick={onOpen} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: 'none', border: 'none', cursor: 'pointer',
      color: '#7c8dc1', fontSize: 11, fontWeight: 600, padding: '2px 4px 2px 0',
      borderRadius: 4, transition: 'color 0.15s', marginTop: 2,
    }}
      onMouseEnter={e => e.currentTarget.style.color = '#a5b4fc'}
      onMouseLeave={e => e.currentTarget.style.color = '#7c8dc1'}
    >
      <span>🧵</span>
      <span>{count} {count === 1 ? 'reply' : 'replies'}</span>
      {when && <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>· Last reply {when}</span>}
    </button>
  );
}

// ── Reply quote ───────────────────────────────────────────────────────────────

function ReplyQuote({ replyTo, onJump }) {
  if (!replyTo) return null;
  const name = replyTo.senderName || replyTo.author || 'Unknown';
  const preview = (replyTo.text || replyTo.content || '').slice(0, 80);
  return (
    <div className="message-reply-quote" onClick={onJump}>
      <div className="reply-quote-line" />
      <span className="reply-quote-author">↩ {name}</span>
      <span className="reply-quote-preview">{preview || 'Click to see attachment'}</span>
    </div>
  );
}

// ── Reactions bar ─────────────────────────────────────────────────────────────

function ReactionsBar({ reactions, messageId }) {
  const { user } = useAeThex();
  const addReaction = useMessageStore(s => s.addReaction);
  const removeReaction = useMessageStore(s => s.removeReaction);
  if (!reactions?.length) return null;

  return (
    <div className="message-reactions">
      {reactions.map(r => {
        const mine = user?.id && r.users.includes(user.id);
        return (
          <button
            key={r.emoji}
            className={`reaction-chip${mine ? ' active' : ''}`}
            title={r.users.length > 3 ? r.users.join(', ') : undefined}
            onClick={() => mine
              ? removeReaction(messageId, r.emoji, user?.id)
              : addReaction(messageId, r.emoji, user?.id)}
          >
            {r.emoji} <span className="reaction-count">{r.users.length}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Emoji quick-pick ──────────────────────────────────────────────────────────

function EmojiQuickPick({ onPick, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  return (
    <div ref={ref} className="emoji-quickpick">
      {QUICK_REACTIONS.map(e => (
        <button key={e} className="emoji-quickpick-btn" onClick={() => { onPick(e); onClose(); }}>{e}</button>
      ))}
    </div>
  );
}

// ── Main Message component ────────────────────────────────────────────────────

export default function Message(props) {
  const body        = props.text || props.content || '';
  const authorName  = props.author || props.senderName || 'Unknown';
  const displayTime = props.time || (props.timestamp ? new Date(props.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
  const isEdited    = props.isEdited || props.edited;
  const avatarText  = props.avatar || authorName?.[0]?.toUpperCase() || '?';
  const reactions   = props.reactions || [];

  const [hovered,      setHovered]      = useState(false);
  const [showEmoji,    setShowEmoji]    = useState(false);
  const [isEditing,    setIsEditing]    = useState(false);
  const [editContent,  setEditContent]  = useState(body);
  const [ctxMenu,      setCtxMenu]      = useState(null); // { x, y }
  const bookmarks   = useBookmarks();
  const hoverCard   = useHoverCard();
  const authorRef   = useRef(null);

  const saveEditedMessage = useMessageStore(s => s.saveEditedMessage);
  const deleteMessage     = useMessageStore(s => s.deleteMessage);
  const pinMessage        = useMessageStore(s => s.pinMessage);
  const addReaction       = useMessageStore(s => s.addReaction);
  const getCurrentUser    = useMemberStore(s => s.getCurrentUser);
  const onOpen            = useModalStore(s => s.onOpen);
  const { user }          = useAeThex();

  let currentUser = null;
  try { currentUser = getCurrentUser(); } catch (_) {}
  const currentId = user?.id || currentUser?.id;

  const isOwnMessage = currentUser && (
    authorName === currentUser.name ||
    authorName === currentUser.username ||
    props.senderId === currentId
  );
  const isAdmin = currentUser?.role?.toUpperCase() === 'ADMIN' || currentUser?.role?.toUpperCase() === 'OWNER';

  const handleSaveEdit = () => {
    if (editContent.trim()) { saveEditedMessage(props.id, editContent); setIsEditing(false); }
  };
  const handleDelete = () => { if (confirm('Delete this message?')) deleteMessage(props.id); };
  const handlePin    = () => pinMessage(props.id);
  const handleReact  = (emoji) => addReaction(props.id, emoji, currentId);

  // ── Grouped (continuation) message ──
  if (props.grouped) {
    return (
      <div
        id={`msg-${props.id}`}
        className="message-item grouped"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { setHovered(false); setShowEmoji(false); }}
        style={{ position: 'relative' }}
      >
        <span className="grouped-time" style={{ opacity: hovered ? 1 : 0 }}>{displayTime}</span>
        <div className="message-content" style={{ paddingLeft: 0 }}>
          {isEditing ? (
            <EditBox value={editContent} onChange={setEditContent} onSave={handleSaveEdit} onCancel={() => { setIsEditing(false); setEditContent(body); }} />
          ) : (
            <>
              {props.poll ? <PollMessage poll={props.poll} /> : <MarkdownContent text={editContent} />}
              {isEdited && !props.poll && <span className="message-edited"> (edited)</span>}
              <Attachments attachments={props.attachments} text={editContent} />
              <UrlEmbeds text={editContent} />
            </>
          )}
          <ReactionsBar reactions={reactions} messageId={props.id} />
        </div>
        {hovered && !isEditing && (
          <MessageToolbar
            isOwnMessage={isOwnMessage} isAdmin={isAdmin}
            showEmoji={showEmoji} setShowEmoji={setShowEmoji}
            onReact={handleReact} onReply={() => props.onReply?.(props)} onThread={() => props.onThread?.(props)}
            onEdit={() => setIsEditing(true)} onDelete={handleDelete} onPin={handlePin}
          />
        )}
      </div>
    );
  }

  // ── System message ──
  if (props.type === 'system') {
    const cls = (props.label || props.badge || '').toLowerCase();
    return (
      <div className={`message-system ${cls}`}>
        <div className={`system-label ${cls}`}>[{props.label || props.badge}] System Announcement</div>
        <div>{body}</div>
      </div>
    );
  }

  // ── Full message ──
  const handleJump = (targetId) => {
    const el = document.getElementById(`msg-${targetId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('msg-highlight');
    setTimeout(() => el.classList.remove('msg-highlight'), 2000);
  };

  const authorMember = { name: authorName, username: authorName, role: props.role, badge: props.badge, status: props.status, avatar: avatarText };
  const isBookmarked = bookmarks.has(props.id);
  const ctxItems = [
    { icon: '↩',  label: 'Reply',             onClick: () => props.onReply?.(props) },
    { icon: '🧵', label: 'Reply in Thread',    onClick: () => props.onThread?.(props) },
    { icon: '😊', label: 'Add Reaction',       onClick: () => setShowEmoji(true) },
    { divider: true },
    { icon: isBookmarked ? '🔖' : '🔖', label: isBookmarked ? 'Remove Bookmark' : 'Bookmark', onClick: () => isBookmarked ? bookmarks.remove(props.id) : bookmarks.add(props) },
    ...(isOwnMessage ? [{ icon: '✏️', label: 'Edit Message', onClick: () => setIsEditing(true) }] : []),
    ...(isOwnMessage || isAdmin ? [{ icon: '📌', label: 'Pin Message', onClick: handlePin }] : []),
    { icon: '📋', label: 'Copy Text',          onClick: () => navigator.clipboard?.writeText(body) },
    { icon: '🔗', label: 'Copy Message ID',    onClick: () => navigator.clipboard?.writeText(props.id || '') },
    { divider: true },
    { icon: '🚩', label: 'Report Message',     onClick: () => onOpen('report', { message: props }), danger: true },
    ...(isOwnMessage || isAdmin ? [{ icon: '🗑️', label: 'Delete Message', onClick: handleDelete, danger: true }] : []),
  ];

  return (
    <div
      id={`msg-${props.id}`}
      className="message"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setShowEmoji(false); hoverCard.onLeave(); }}
      onContextMenu={e => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY }); }}
    >
      {ctxMenu && <ContextMenu x={ctxMenu.x} y={ctxMenu.y} items={ctxItems} onClose={() => setCtxMenu(null)} />}
      {hoverCard.card && (
        <UserHoverCard
          member={hoverCard.card.member}
          anchorRef={hoverCard.card.anchorRef}
          onClose={hoverCard.onClose}
          onViewProfile={() => { hoverCard.onClose(); onOpen('userProfile', { member: hoverCard.card.member }); }}
          onMessage={() => { hoverCard.onClose(); onOpen('directMessage', { member: hoverCard.card.member }); }}
        />
      )}
      {/* Reply quote header */}
      {props.replyTo && <ReplyQuote replyTo={props.replyTo} onJump={() => handleJump(props.replyTo.id)} />}

      <div
        className={`message-avatar ${props.avatarBg || ''}`}
        ref={authorRef}
        onMouseEnter={() => hoverCard.onEnter(authorMember, authorRef)}
        onMouseLeave={hoverCard.onLeave}
        onClick={() => onOpen('userProfile', { member: authorMember })}
        style={{ cursor: 'pointer', alignSelf: 'flex-start', marginTop: props.replyTo ? 4 : 0 }}
      >
        {avatarText}
      </div>

      <div className="message-content">
        <div className="message-header">
          <span
            ref={authorRef}
            className="message-author"
            onMouseEnter={() => hoverCard.onEnter(authorMember, authorRef)}
            onMouseLeave={hoverCard.onLeave}
            onClick={() => onOpen('userProfile', { member: authorMember })}
            style={{ cursor: 'pointer' }}
          >
            {authorName}
          </span>
          {props.badge && (
            <span className={`message-badge ${(props.badge || '').toLowerCase()}`}>{props.badge}</span>
          )}
          <span className="message-time">{displayTime}</span>
          {isEdited && !isEditing && <span className="message-edited">(edited)</span>}
        </div>

        {isEditing ? (
          <EditBox value={editContent} onChange={setEditContent} onSave={handleSaveEdit} onCancel={() => { setIsEditing(false); setEditContent(body); }} />
        ) : (
          <>
            {props.poll ? <PollMessage poll={props.poll} /> : <MarkdownContent text={editContent} />}
            <Attachments attachments={props.attachments} text={editContent} />
            <UrlEmbeds text={editContent} />
          </>
        )}

        <ReactionsBar reactions={reactions} messageId={props.id} />
        <ThreadReplyBadge
          count={props.threadCount || props.replyCount || props.replies?.length || 0}
          lastReplyAt={props.lastReplyAt || props.threadLastReply}
          onOpen={() => props.onThread?.(props)}
        />
      </div>

      {hovered && !isEditing && (
        <MessageToolbar
          isOwnMessage={isOwnMessage} isAdmin={isAdmin}
          showEmoji={showEmoji} setShowEmoji={setShowEmoji}
          onReact={handleReact} onReply={() => props.onReply?.(props)} onThread={() => props.onThread?.(props)}
          onEdit={() => setIsEditing(true)} onDelete={handleDelete} onPin={handlePin}
        />
      )}
    </div>
  );
}

// ── Toolbar ───────────────────────────────────────────────────────────────────

function MessageToolbar({ isOwnMessage, isAdmin, showEmoji, setShowEmoji, onReact, onReply, onThread, onEdit, onDelete, onPin }) {
  return (
    <div className="message-toolbar">
      {showEmoji && <EmojiQuickPick onPick={onReact} onClose={() => setShowEmoji(false)} />}
      <button className="toolbar-btn" title="Add reaction" onClick={() => setShowEmoji(v => !v)}>😊</button>
      <button className="toolbar-btn" title="Reply in thread" onClick={onThread}>🧵</button>
      <button className="toolbar-btn" title="Quote reply" onClick={onReply}>↩</button>
      {(isOwnMessage || isAdmin) && (
        <button className="toolbar-btn" title="Pin message" onClick={onPin}>📌</button>
      )}
      {isOwnMessage && (
        <button className="toolbar-btn" title="Edit message" onClick={onEdit}>✏️</button>
      )}
      {(isOwnMessage || isAdmin) && (
        <button className="toolbar-btn toolbar-btn-danger" title="Delete message" onClick={onDelete}>🗑️</button>
      )}
    </div>
  );
}

// ── Edit box ──────────────────────────────────────────────────────────────────

function EditBox({ value, onChange, onSave, onCancel }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  return (
    <div className="message-edit">
      <textarea
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSave(); } if (e.key === 'Escape') onCancel(); }}
        className="message-edit-input"
        rows={Math.min(value.split('\n').length, 8)}
        style={{ resize: 'none' }}
      />
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Enter to save · Esc to cancel</span>
        <button onClick={onSave} className="message-edit-save">Save</button>
        <button onClick={onCancel} className="message-edit-cancel">Cancel</button>
      </div>
    </div>
  );
}
